import { cidrContainsIp, parseCidr, parseIp } from './ip';

/**
 * Classifying a site's NAT situation from what it observed.
 *
 * The purpose of the whole exercise is to decide how two peers can connect:
 * directly, by punching a hole through their NATs, or through a relay. Getting
 * that wrong is expensive in two opposite directions. Calling a symmetric NAT
 * cone means attempting a hole-punch that can never succeed, so the tunnel
 * silently never comes up. Calling a cone NAT symmetric means routing traffic
 * through a paid relay that was never needed. So the classification is
 * conservative in a specific way: it claims a NAT is hole-punchable only when it
 * has enough evidence to be sure, and otherwise assumes relay — because a
 * connection that is slower than necessary is a far better failure than one that
 * never establishes.
 */

/** Shared CGNAT space (RFC 6598). A public-looking address nothing outside can reach. */
const CGNAT_CIDR = parseCidr('100.64.0.0/10');
const PRIVATE_V4 = ['10.0.0.0/8', '172.16.0.0/12', '192.168.0.0/16'].map((c) => parseCidr(c));
const PRIVATE_V6 = [parseCidr('fc00::/7')];

function isCgnat(ip: string): boolean {
  const addr = parseIp(ip);
  return addr !== null && CGNAT_CIDR !== null && cidrContainsIp(CGNAT_CIDR, addr);
}

/** Private (RFC1918 / ULA) or CGNAT — anything not globally reachable. */
export function isPrivateOrShared(ip: string): boolean {
  const addr = parseIp(ip);
  if (addr === null) return false;
  const ranges = addr.family === 4 ? PRIVATE_V4 : PRIVATE_V6;
  return isCgnat(ip) || ranges.some((cidr) => cidr !== null && cidrContainsIp(cidr, addr));
}

/** One reflector's view of this edge's external mapping. */
export interface NatObservation {
  reflector: string;
  reachable: boolean;
  /** The external address the reflector saw this edge come from. */
  externalIp: string | null;
  /** The external port the reflector saw. */
  externalPort: number | null;
}

export interface NatInput {
  /** Results from UDP reachability tests against several reflectors. */
  observations: NatObservation[];
  /** The address on the edge's own WAN interface. */
  localAddress: string;
  /** Whether the edge has a globally routable IPv6 address. */
  hasGlobalIpv6: boolean;
}

/** How this NAT maps an internal endpoint to an external one. */
export const NAT_MAPPINGS = [
  /** No NAT: the external address is the edge's own. */
  'DIRECT',
  /** One external mapping regardless of destination. Hole-punchable. */
  'ENDPOINT_INDEPENDENT',
  /** A different external port per destination. Not hole-punchable. */
  'ENDPOINT_DEPENDENT',
  /** No reflector answered: UDP is blocked or the link is down. */
  'BLOCKED',
  /** Too few reflectors answered to tell independent from dependent. */
  'UNKNOWN',
] as const;
export type NatMapping = (typeof NAT_MAPPINGS)[number];

/**
 * The fewest reflectors that can distinguish an endpoint-independent mapping
 * from a dependent one.
 *
 * One reflector sees one mapping and cannot tell whether a second destination
 * would see the same. Claiming cone from a single observation is how a symmetric
 * NAT gets mistaken for hole-punchable.
 */
export const MIN_REFLECTORS_FOR_MAPPING = 2;

export interface NatProfile {
  mapping: NatMapping;
  /** Behind carrier-grade NAT: a public-looking address nothing can reach. */
  cgnat: boolean;
  /** Behind more than one layer of NAT. */
  doubleNat: boolean;
  /** The external address peers would target, or null when none is known. */
  externalIp: string | null;
  externalPort: number | null;
  /** Whether a peer can reasonably attempt to hole-punch to this edge. */
  holePunchable: boolean;
  /** Whether a direct IPv6 path avoids the NAT entirely. */
  preferIpv6: boolean;
  reason: string;
}

/**
 * Classify a NAT from its observations.
 *
 * Pure. The mapping is decided from whether reachable reflectors agree on the
 * external port: agreement means the mapping does not depend on the destination
 * (cone, hole-punchable); disagreement means it does (symmetric, relay). Fewer
 * than two reachable reflectors is UNKNOWN, not cone.
 */
export function classifyNat(input: NatInput): NatProfile {
  const reachable = input.observations.filter(
    (o) => o.reachable && o.externalIp !== null && o.externalPort !== null,
  );

  const externalIp = reachable[0]?.externalIp ?? null;
  const externalPort = reachable[0]?.externalPort ?? null;
  const cgnat = externalIp !== null && isCgnat(externalIp);
  // Double NAT: the interface address is private and the external address is
  // still not globally reachable, so there is at least one NAT above the one on
  // this host.
  const doubleNat =
    isPrivateOrShared(input.localAddress) && externalIp !== null && isPrivateOrShared(externalIp);
  const preferIpv6 = input.hasGlobalIpv6;

  if (reachable.length === 0) {
    return {
      mapping: 'BLOCKED',
      cgnat,
      doubleNat,
      externalIp: null,
      externalPort: null,
      // A globally reachable IPv6 path does not need the NATed IPv4 to work.
      holePunchable: false,
      preferIpv6,
      reason: preferIpv6
        ? 'No reflector answered over IPv4, but a direct IPv6 path is available.'
        : 'No reflector answered: UDP is blocked or the link is down. A relay is required.',
    };
  }

  // No NAT at all: the external address is the edge's own and is public.
  if (externalIp === input.localAddress && !isPrivateOrShared(externalIp)) {
    return {
      mapping: 'DIRECT',
      cgnat: false,
      doubleNat: false,
      externalIp,
      externalPort,
      holePunchable: true,
      preferIpv6,
      reason: 'The edge has a public address and is not behind NAT.',
    };
  }

  if (reachable.length < MIN_REFLECTORS_FOR_MAPPING) {
    return {
      mapping: 'UNKNOWN',
      cgnat,
      doubleNat,
      externalIp,
      externalPort,
      // Not enough evidence to promise hole-punching. Assume relay rather than
      // attempt a punch that may never land.
      holePunchable: false,
      preferIpv6,
      reason: `Only ${reachable.length} reflector answered; cannot tell a hole-punchable NAT from a symmetric one, so a relay is assumed.`,
    };
  }

  const ports = new Set(reachable.map((o) => o.externalPort));
  if (ports.size === 1) {
    return {
      mapping: 'ENDPOINT_INDEPENDENT',
      cgnat,
      doubleNat,
      externalIp,
      externalPort,
      // Even a hole-punchable NAT under CGNAT usually cannot be reached, because
      // the carrier's NAT is symmetric and shared; treat CGNAT as relay-only.
      holePunchable: !cgnat && !doubleNat,
      preferIpv6,
      reason: cgnat
        ? 'The mapping is consistent, but the edge is behind carrier-grade NAT, which a peer cannot punch through. A relay is required.'
        : 'The external mapping is the same to every reflector, so a peer can hole-punch to it.',
    };
  }

  return {
    mapping: 'ENDPOINT_DEPENDENT',
    cgnat,
    doubleNat,
    externalIp,
    externalPort,
    holePunchable: false,
    preferIpv6,
    reason:
      'The external port changes per destination (symmetric NAT), which cannot be hole-punched. ' +
      'A relay is required.',
  };
}

/**
 * Whether two observations of the same edge, taken at different times, show the
 * NAT rebinding its mapping underneath a live tunnel.
 *
 * A rebinding invalidates a cached endpoint: a peer sending to the old
 * address:port is sending into a hole that closed. It is why persistent
 * keepalive exists — to hold the mapping open — and why a changed mapping must
 * trigger a fresh endpoint exchange rather than being ignored.
 */
export function hasRebound(previous: NatProfile, current: NatProfile): boolean {
  if (previous.externalIp === null || current.externalIp === null) return false;
  return (
    previous.externalIp !== current.externalIp || previous.externalPort !== current.externalPort
  );
}
